import { useQuery } from '@tanstack/vue-query';
import AdyenPlugin, { posSessionTarget } from '@/capacitor-plugins/AdyenPlugin';
import { Capacitor } from '@capacitor/core';
import { computed, type MaybeRefOrGetter, onMounted, ref, toValue } from 'vue';

let adyenPosSessionInit: Promise<boolean> | null = null;

export function ensureAdyenPosSession(): Promise<boolean> {
  if (!Capacitor.isPluginAvailable('Adyen')) {
    return Promise.resolve(false);
  }

  if (adyenPosSessionInit) {
    return adyenPosSessionInit;
  }

  adyenPosSessionInit = (async () => {
    try {
      const { needsTapToPayLinking } = await AdyenPlugin.needsTapToPayLinking();
      await AdyenPlugin.initPosSession().catch((error) => {
        console.error('Failed to initialize Adyen POS session', error);
      });
      if (needsTapToPayLinking) {
        await AdyenPlugin.linkTapToPay().catch((error) => {
          console.error('Failed to link tap to pay', error);
        });
      }
      return true;
    } finally {
      adyenPosSessionInit = null;
    }
  })();

  return adyenPosSessionInit;
}

type PosSessionBooking = { id: string; legalEntity: { id: string } };

let lastPosSessionOpen: Promise<unknown> = Promise.resolve();

/**
 * Opens the POS session for the given booking, on the merchant account of its legal entity. A
 * session of another legal entity is reset first, so a payment never lands on another company's
 * merchant account.
 *
 * Calls run one after another: the payment buttons can be tapped while the warm-up on page open is
 * still running, and a second reset would tear down the session that warm-up is opening.
 */
export function ensureAdyenPosSessionFor(booking: PosSessionBooking): Promise<boolean> {
  const open = lastPosSessionOpen.then(async () => {
    if (!Capacitor.isPluginAvailable('Adyen')) {
      return false;
    }
    posSessionTarget.bookingId = booking.id;
    if (posSessionTarget.legalEntityId !== booking.legalEntity.id) {
      await AdyenPlugin.resetSession();
      posSessionTarget.legalEntityId = booking.legalEntity.id;
    }
    return ensureAdyenPosSession();
  });
  lastPosSessionOpen = open.catch(() => undefined);
  return open;
}

/** Payment methods of the POS session, opened for the given booking if any. */
export function useAdyenPaymentMethods(booking?: MaybeRefOrGetter<PosSessionBooking | undefined>) {
  const shouldLoadPaymentMethods = ref(false);

  onMounted(async () => {
    const target = toValue(booking);
    shouldLoadPaymentMethods.value = target
      ? await ensureAdyenPosSessionFor(target)
      : await ensureAdyenPosSession();
  });

  return useQuery({
    queryKey: ['adyenMethods'],
    queryFn: () => AdyenPlugin.getMethods(),
    enabled: computed(() => shouldLoadPaymentMethods.value),
  });
}
